import { CarPageContextProvider } from '@/components/car/CarPageContext';
import Driving from '@/components/car/Driving';
import { Faq } from '@/components/car/Faq';
import Footer from '@/components/car/Footer';
import { CustomersReviews } from '@/components/shared';
import { PageWrapper } from '@/components/shared/Content';
import Layout, { CheckOurPricesCTA } from '@/components/shared/Layout';
import { SEO } from '@/components/shared/SEO';
import useSmoothScroll from '@/hooks/useSmoothScroll';
import type { Config } from '@/pageConfig/car';
import { CAR_LANDING_PAGE } from '@/utils/productAdaPages';
import { PRODUCT_CAR } from '@/utils/products';
import { Box, Flex, themedColor, useResponsiveValue } from '@lemonade-hq/cdk';
import dynamic from 'next/dynamic';
import React, { useEffect, useState } from 'react';
import styled, { createGlobalStyle, css } from 'styled-components';

const LowMilage = dynamic(() => import('@/components/car/LowMilage'));
const Forest = dynamic(() => import('@/components/car/Forest'), { ssr: false });
const LowMilagePpm = dynamic(() => import('@/components/car/LowMilagePpm'));
const ElectricCoverages = dynamic(() => import('@/components/car/ElectricCoverages'));
const OverallCoverages = dynamic(() => import('@/components/car/OverallCoverages'));
const BasicCoverages = dynamic(() => import('@/components/car/BasicCoverages'));
const Claims = dynamic(() => import('@/components/car/Claims'));
const Bundles = dynamic(() => import('@/components/car/Bundles'));

interface CarTemplateProps {
  readonly config: Config;
  readonly isNjEnabled: boolean;
  readonly isEmbeddedPage?: boolean;
  readonly priceType?: 'ppm' | 'fixed';
}

interface CarPageSectionProps extends Pick<CarTemplateProps, 'config'> {
  isEmbeddedPage?: boolean;
}

const GlobalStyle = createGlobalStyle`
    html {
        scroll-behavior: smooth;
    }
`;

const CarPageSection: React.FC<CarPageSectionProps> = ({ isEmbeddedPage = false, config }) => {
  return (
    <>
      <GlobalStyle />
      <PageWrapper>
        <Flex textAlign="center" flexDirection="column" alignItems="center" overflow="hidden">
          <Driving {...config.hero} />
          <OverallCoverages {...config.overallCoverages} />
          {'lowMilagePpm' in config && <LowMilagePpm {...config?.lowMilagePpm} />}
          <BasicCoverages {...config.basicCoverages} />
          {'lowMilage' in config && <LowMilage {...config.lowMilage} />}
          {'electricCoverages' in config && <ElectricCoverages {...config.electricCoverages} />}
          <Forest />
          {!isEmbeddedPage && <Bundles />}
          <Claims />
          <CustomersReviews {...config.customersReviews} />
          <Faq {...config.faq} />
          <Footer />
        </Flex>
      </PageWrapper>
    </>
  );
};

const PPM_SUPPORTED_STATES = ['OR', 'AZ'];

const StyledCheckOurPricesButton = styled(CheckOurPricesCTA)<{
  green: boolean;
}>`
  &&& {
    transition: background-color 200ms ease-out;
    box-shadow: none !important;

    ${(props: { green: boolean }) =>
      props.green &&
      css`
        background-color: ${themedColor('green')} !important;

        &:hover,
        &:active {
          background-color: hsl(172, 66%, 42%) !important;
        }
      `}
  }
`;

interface CheckOurPricesButtonProps {
  priceType: 'ppm' | 'fixed';
  checkOurPrices: {
    long: { plural: string };
    short: { plural: string };
  };
}

const CheckOurPricesButton = ({ priceType, checkOurPrices }: CheckOurPricesButtonProps) => {
  const { absoluteScroll } = useSmoothScroll();
  const greenButtonRange = useResponsiveValue([
    [1400, 4200],
    [3100, 3600],
    [2500, 6250],
  ]);
  const greenButtonRangePpm = useResponsiveValue([
    [1800, 4200],
    [3100, 3600],
    [3300, 5500],
  ]);
  const greenButtonEnabled =
    priceType === 'ppm'
      ? absoluteScroll >= greenButtonRangePpm[0] && absoluteScroll <= greenButtonRangePpm[1]
      : absoluteScroll >= greenButtonRange[0] && absoluteScroll <= greenButtonRange[1];

  return (
    <StyledCheckOurPricesButton
      product={PRODUCT_CAR}
      size="small"
      flavor="primary"
      green={greenButtonEnabled}
      aria-label={checkOurPrices.long.plural}
      data-event="menu_check_prices"
      data-title-desktop={checkOurPrices.long.plural}
      data-title-mobile={checkOurPrices.short.plural}
      data-event-param-product={PRODUCT_CAR}
      // eslint-disable-next-line react/no-children-prop
      children={null}
    />
  );
};

const addOrRemoveState = (states: string[], state: string, shouldEnable: boolean): string[] => {
  if (shouldEnable && !states.includes(state)) {
    return [...states, state];
  }
  return states.filter(s => s !== state);
};

const TEXAS_EXTRA_DISCLAIMER = `
Texas car policies are administered and serviced by Lemonade Insurance Agency, LLC and underwritten by Home State County Mutual Insurance Company, 4315 Lake Shore Drive, Suite J, Waco, Texas 76710.`;

export const carExtraDisclaimer = (isTexas?: boolean) =>
  `Car insurance features, prices, coverages, and available discounts may vary by state and are subject to qualifications. Coverage is subject to policy terms and conditions and may not be available in all states.
${isTexas ? TEXAS_EXTRA_DISCLAIMER : ''}
`;

export const CarPageTemplate: React.FC<CarTemplateProps> = ({
  config,
  priceType = 'fixed',
  isEmbeddedPage = false,
  isNjEnabled,
}) => {
  // TODO: check from xs as well, embedded and isNjEnabled, since both are coming in query on SSR props in current marketing
  const [ppmSupportedStates, setPpmSupportedStates] = useState<string[]>(PPM_SUPPORTED_STATES);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const states = addOrRemoveState([...ppmSupportedStates], 'NJ', isNjEnabled);
    setPpmSupportedStates(states);
  }, []);

  const { checkOurPrices } = config.layout.headerActions.CTAs;
  const isTexas = ppmSupportedStates.includes('TX');

  const extraDisclaimer = carExtraDisclaimer(isTexas);

  return (
    <CarPageContextProvider isEmbeddedPage={isEmbeddedPage}>
      <SEO {...config.seo} />
      {isEmbeddedPage ? (
        <Box pt={60}>
          <CarPageSection config={config} isEmbeddedPage={isEmbeddedPage} />
        </Box>
      ) : (
        <Layout
          page={CAR_LANDING_PAGE}
          productType={PRODUCT_CAR}
          product={PRODUCT_CAR}
          headerActions={config.layout.headerActions}
          alignLogoOnMobile={config.layout.alignLogoOnMobile}
          showBackToYourQuote={false}
          extraFooterDisclaimer={extraDisclaimer}
          CustomHeaderCTA={<CheckOurPricesButton priceType={priceType} checkOurPrices={checkOurPrices} />}
        >
          <CarPageSection config={config} />
        </Layout>
      )}
    </CarPageContextProvider>
  );
};
